import type { Photo } from '@capacitor/camera';

export interface SelectedImage {
  dataUrl: string;
  name: string;
  type: string;
  size: number;
}

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

const FORMAT_TO_MIME_TYPE: Record<string, string> = {
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const estimateBase64Bytes = (base64Payload: string): number => {
  const sanitized = base64Payload.replace(/\s/g, '');
  const padding = sanitized.endsWith('==') ? 2 : sanitized.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((sanitized.length * 3) / 4) - padding);
};

const normalizeMimeType = (mimeType?: string | null, format?: string | null): string => {
  const normalizedMimeType = mimeType?.toLowerCase().trim();
  if (normalizedMimeType && ALLOWED_IMAGE_TYPES.has(normalizedMimeType)) {
    return normalizedMimeType;
  }

  const normalizedFormat = format?.toLowerCase().trim();
  if (normalizedFormat && FORMAT_TO_MIME_TYPE[normalizedFormat]) {
    return FORMAT_TO_MIME_TYPE[normalizedFormat];
  }

  return '';
};

const getExtensionForMimeType = (mimeType: string): string => {
  return MIME_TYPE_TO_EXTENSION[mimeType] || 'jpg';
};

const buildGeneratedFileName = (mimeType: string): string => {
  return `nameplate-${Date.now()}.${getExtensionForMimeType(mimeType)}`;
};

const extractFileNameFromPath = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const withoutQuery = value.split('?')[0];
  const segments = withoutQuery.split('/');
  return segments[segments.length - 1] || '';
};

const validateImage = (mimeType: string, sizeInBytes: number) => {
  if (!ALLOWED_IMAGE_TYPES.has(mimeType)) {
    throw new Error('Unsupported image type. Use PNG, JPG, WEBP, or GIF.');
  }

  if (sizeInBytes > MAX_IMAGE_BYTES) {
    throw new Error('Image is too large. Max size is 5 MB.');
  }
};

export const readImageFile = (file: File): Promise<SelectedImage> => {
  return new Promise((resolve, reject) => {
    try {
      validateImage(file.type, file.size);
    } catch (error) {
      reject(error);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        reject(new Error('Failed to read image file.'));
        return;
      }

      resolve({
        dataUrl: result,
        name: file.name || 'image',
        type: file.type,
        size: file.size,
      });
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
};

export const buildSelectedImageFromCameraPhoto = (photo: Photo): SelectedImage => {
  const mimeType = normalizeMimeType(photo.format ? `image/${photo.format}` : undefined, photo.format);
  if (!mimeType) {
    throw new Error('Unsupported image type. Use PNG, JPG, WEBP, or GIF.');
  }

  const base64Payload = photo.base64String
    || (photo.dataUrl?.includes(',') ? photo.dataUrl.split(',')[1] : '');
  const dataUrl = photo.dataUrl || (base64Payload ? `data:${mimeType};base64,${base64Payload}` : '');

  if (!dataUrl || !base64Payload) {
    throw new Error('Failed to read image file.');
  }

  const size = estimateBase64Bytes(base64Payload);
  validateImage(mimeType, size);

  const originalName = extractFileNameFromPath(photo.path) || extractFileNameFromPath(photo.webPath);
  const hasFileExtension = /\.[a-z0-9]+$/i.test(originalName);
  const name = originalName
    ? (hasFileExtension ? originalName : `${originalName}.${getExtensionForMimeType(mimeType)}`)
    : buildGeneratedFileName(mimeType);

  return {
    dataUrl,
    name,
    type: mimeType,
    size,
  };
};

export const isCameraCancellationError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error || '');
  const normalizedMessage = message.toLowerCase();

  return normalizedMessage.includes('cancel')
    || normalizedMessage.includes('canceled')
    || normalizedMessage.includes('no image picked');
};

export const getImageSelectionErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const normalizedMessage = error.message.toLowerCase();
    if (normalizedMessage.includes('permission')) {
      return 'Camera or photo access was denied. Please enable access in your device settings.';
    }
    return error.message;
  }

  return 'Invalid image upload.';
};
